import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { formatNumber } from "../../utils/formatNumber";
import AlertModal from "../AlertModal";
import { useAccount, useSecretNetworkClient } from "../../models/account";
import {
  confirmOffChainTransfer,
  enterP2PDeal,
  fetchDealPaymentInfo,
  P2PConfig,
  P2PDeal,
  P2PPaymentInfo,
  P2PPost,
} from "../../models/p2p";
import PostCard from "./PostCard";
import useIsMobile from "../../utils/useIsMobile";
import { useFinaTokenInfo } from "../../models/finaTokenInfo";

interface Props {
  open: boolean;
  onClose: () => void;
  deal?: P2PDeal;
  config?: P2PConfig;
  refetch?: () => void;
}

const BankTransferModal: FC<Props> = ({
  open,
  onClose,
  deal,
  config,
  refetch,
}) => {
  const secretjs = useSecretNetworkClient();
  const { finaToken } = useFinaTokenInfo();
  const isMobile = useIsMobile();
  const { account } = useAccount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [paymentInfo, setPaymentInfo] = useState<P2PPaymentInfo>();

  useEffect(() => {
    if (open && secretjs && config && deal) {
      fetchDealPaymentInfo(
        {
          contract_address: config.contract_address,
          code_hash: config.code_hash,
          address: account.address,
          viewing_key: finaToken.viewingKey,
          deal_id: deal.deal_id,
        },
        secretjs
      ).then(setPaymentInfo);
    }
  }, [open, deal, account.address, config, finaToken.viewingKey, secretjs]);

  useEffect(() => {
    if (refetch && (success || error)) {
      refetch();
    }
  }, [refetch, success, error]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Payment Detail</DialogTitle>
        <DialogContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            my={3}
          >
            <Typography variant="body2">Payment Method</Typography>
            <Typography variant="h6">{paymentInfo?.method || ""}</Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            my={3}
          >
            <Typography variant="body2">Payment Detail</Typography>
            <Typography variant="h6">{paymentInfo?.detail}</Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            my={3}
          >
            <Typography variant="body2">Amount</Typography>
            <Typography variant="h6">
              {formatNumber(
                (((deal?.settle_price || 0) / 10 ** (config?.decimals || 6)) *
                  (deal?.amount || 0)) /
                  10 ** (config?.decimals || 6)
              )}{" "}
              {deal?.settle_currency?.currency}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            my={3}
          >
            <Typography variant="body2">Trader Contact</Typography>
            <Typography variant="h6">{paymentInfo?.contact}</Typography>
          </Stack>
          {(deal?.state || "").includes("bank_transfer") && (
            <>
              <Typography variant="body2" gutterBottom>
                Please click <b>Confirm Payment</b> after making payment with
                the information above
              </Typography>
              <Typography variant="body2">
                * Make sure to contact the trader before making payment.
              </Typography>
              <Typography variant="body2">
                * Make sure to keep all chat records related to the trade in
                case of dispute.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button sx={{ mr: 1 }} variant="text" size="large" onClick={onClose}>
            Close
          </Button>
          {(deal?.state || "").includes("bank_transfer") && (
            <Button
              disabled={loading}
              size="large"
              onClick={async () => {
                try {
                  setLoading(true);
                  await confirmOffChainTransfer(
                    {
                      contract_address: config?.contract_address || "",
                      code_hash: config?.code_hash || "",
                      address: account.address,
                      deal_id: deal?.deal_id || "",
                    },
                    secretjs!
                  );
                  setLoading(false);
                  setSuccess("You have successfully confirmed your payment!");
                  onClose();
                } catch (err: any) {
                  setLoading(false);
                  setError(err.message);
                }
              }}
            >
              {loading ? (
                <CircularProgress sx={{ mx: 1.5 }} size={25} />
              ) : (
                "Confirm Payment"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <AlertModal
        open={!!success}
        onClose={() => setSuccess("")}
        title="Success"
        description={success}
        type="success"
      />
      <AlertModal
        open={!!error}
        onClose={() => setError("")}
        title="Error"
        description={error}
        type="error"
      />
    </>
  );
};

export default BankTransferModal;
